/**
 * SageConversationMode — hands-free voice loop with Sage.
 *
 * Renders as a compact card pinned to the FAB slot (bottom-right), NOT as
 * a fullscreen overlay. Conceptually it replaces the FAB while active.
 *
 * Loop state machine:
 *   listening → (VAD detects speech) → speaking → (silence for SILENCE_MS)
 *     → transcribing → sending → waiting_for_reply → reply_playback (TTS)
 *     → listening   (repeat until user hits Stop)
 *
 * VAD: AnalyserNode RMS, thresholds from useSageConversationSettings.
 *
 * Reply handling: POST /sage/messages. If `response` returns synchronously
 * use it; otherwise poll /sage/messages for an assistant message newer
 * than the user turn we just sent (same fallback SageChat uses when SSE
 * is unreachable).
 *
 * Audio playback: uses a declaratively-rendered <audio> element to satisfy
 * Safari's autoplay policy across the multi-second TTS fetch.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { X, Mic } from 'lucide-react'
import { tokens } from '@/design-system/tokens'
import { authedFetch, getToken } from '@/lib/api'
import { useSageVoicePreference } from '@/hooks/useSageVoicePreference'
import { useSageConversationSettings, sensitivityThresholds } from '@/hooks/useSageConversationSettings'

const SAGE_SESSION_KEY = 'sage_session_id'

// VAD invariants
const MIN_SPEECH_MS      = 400
const MAX_TURN_MS        = 45000
const POLL_INTERVAL_MS   = 2000
const POLL_TIMEOUT_MS    = 60000

type LoopState =
  | 'initializing'
  | 'listening'
  | 'speaking'
  | 'transcribing'
  | 'waiting_reply'
  | 'reply_playback'
  | 'error'

interface Turn { role: 'user' | 'assistant'; text: string; ts: number }

interface Props {
  open: boolean
  onClose: () => void
  /** Position of the host FAB (bottom-right offsets). Keeps the widget
   *  aligned with wherever the FAB normally sits. */
  fabBottom?: number
  fabRight?: number
}

export const SageConversationMode: React.FC<Props> = ({
  open, onClose, fabBottom = 24, fabRight = 24,
}) => {
  const [voice] = useSageVoicePreference()
  const [convSettings] = useSageConversationSettings()
  const thresholds = sensitivityThresholds(convSettings.sensitivity)
  const SPEAKING_THRESHOLD = thresholds.speaking
  const SILENCE_THRESHOLD  = thresholds.silence
  const SILENCE_MS         = convSettings.silence_ms

  const [state, setState] = useState<LoopState>('initializing')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [rmsLevel, setRmsLevel] = useState(0)

  // Mutable refs — all audio/VAD state lives outside React
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const speakStartRef = useRef<number | null>(null)
  const lastAboveSilenceRef = useRef<number>(0)
  const vadRafRef = useRef<number | null>(null)
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null)
  const ttsUrlRef = useRef<string | null>(null)
  const turnStartRef = useRef<number>(0)
  const abortedRef = useRef(false)

  // ── Cleanup ─────────────────────────────────────────────────────────────
  const killMic = useCallback(() => {
    if (vadRafRef.current !== null) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null }
    if (mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop() } catch { /* no-op */ }
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {})
    }
    audioCtxRef.current = null
    analyserRef.current = null
    speakStartRef.current = null
    lastAboveSilenceRef.current = 0
  }, [])

  const killTTS = useCallback(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause()
      ttsAudioRef.current.removeAttribute('src')
    }
    if (ttsUrlRef.current) { URL.revokeObjectURL(ttsUrlRef.current); ttsUrlRef.current = null }
  }, [])

  const stopAll = useCallback(() => {
    abortedRef.current = true
    killMic()
    killTTS()
  }, [killMic, killTTS])

  // ── Start loop on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    abortedRef.current = false
    // Safari autoplay unlock — touch the audio element inside the gesture
    // that opened the widget.
    ttsAudioRef.current?.play().catch(() => { /* expected, no src */ })
    void startListening()
    return () => { stopAll() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const startListening = async () => {
    setErrorMsg(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      if (abortedRef.current) { stream.getTracks().forEach(t => t.stop()); return }
      streamRef.current = stream

      const AudioCtx = window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AudioCtx) throw new Error('AudioContext unsupported')
      const ctx = new AudioCtx()
      audioCtxRef.current = ctx
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      analyser.smoothingTimeConstant = 0.3
      source.connect(analyser)
      analyserRef.current = analyser

      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm',
      })
      mediaRecorderRef.current = mr
      chunksRef.current = []
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        if (abortedRef.current) return
        const blob = new Blob(chunksRef.current, { type: mr.mimeType })
        chunksRef.current = []
        void handleUserTurn(blob)
      }
      mr.start(250)

      turnStartRef.current = Date.now()
      speakStartRef.current = null
      lastAboveSilenceRef.current = 0
      setState('listening')
      runVAD()
    } catch (err) {
      setErrorMsg(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Mic permission denied' : 'Could not access microphone')
      setState('error')
    }
  }

  const runVAD = () => {
    const analyser = analyserRef.current
    if (!analyser) return
    const buf = new Float32Array(analyser.fftSize)

    const tick = () => {
      if (abortedRef.current || !analyserRef.current) return
      analyser.getFloatTimeDomainData(buf)
      let sum = 0
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
      const rms = Math.sqrt(sum / buf.length)
      setRmsLevel(rms)

      const now = Date.now()

      if (now - turnStartRef.current > MAX_TURN_MS && speakStartRef.current !== null) {
        endUserTurn()
        return
      }

      if (rms > SPEAKING_THRESHOLD) {
        if (speakStartRef.current === null) {
          speakStartRef.current = now
          setState('speaking')
        }
        lastAboveSilenceRef.current = now
      } else if (rms < SILENCE_THRESHOLD) {
        if (speakStartRef.current !== null) {
          const speechDur = now - speakStartRef.current
          const silenceDur = now - lastAboveSilenceRef.current
          if (speechDur > MIN_SPEECH_MS && silenceDur > SILENCE_MS) {
            endUserTurn()
            return
          }
        }
      }
      vadRafRef.current = requestAnimationFrame(tick)
    }
    vadRafRef.current = requestAnimationFrame(tick)
  }

  const endUserTurn = () => {
    if (vadRafRef.current !== null) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null }
    if (mediaRecorderRef.current?.state === 'recording') {
      try { mediaRecorderRef.current.stop() } catch { /* ignore */ }
    }
    setState('transcribing')
  }

  const handleUserTurn = async (blob: Blob) => {
    if (abortedRef.current) return
    if (vadRafRef.current !== null) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null }
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      await audioCtxRef.current.close().catch(() => {})
    }
    audioCtxRef.current = null
    analyserRef.current = null
    mediaRecorderRef.current = null

    let userText = ''
    try {
      const fd = new FormData()
      fd.append('audio', blob, 'turn.webm')
      const res = await authedFetch('/api/v1/sage/transcribe', {
        method: 'POST',
        body: fd,
      }, { redirectOnAuthFailure: false })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(body.error || `transcribe ${res.status}`)
      }
      const data = await res.json() as { text: string }
      userText = (data.text || '').trim()
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Transcription failed')
      setState('error')
      return
    }

    if (abortedRef.current) return
    if (!userText) { void startListening(); return }

    setTurns(prev => [...prev, { role: 'user', text: userText, ts: Date.now() }])
    setState('waiting_reply')

    let assistantText = ''
    const sid = localStorage.getItem(SAGE_SESSION_KEY) || undefined
    const sendTs = Date.now()
    try {
      const res = await authedFetch('/api/v1/sage/messages', {
        method: 'POST',
        body: JSON.stringify({ content: userText, session_id: sid }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) throw new Error(`send ${res.status}`)
      const data = await res.json() as {
        response?: { content: string }
        proxy_pending?: boolean
        session_id?: string
      }
      if (data.session_id) localStorage.setItem(SAGE_SESSION_KEY, data.session_id)
      if (data.response?.content) assistantText = data.response.content
      else if (data.proxy_pending) assistantText = await pollForReply(data.session_id || sid, sendTs)
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Send failed')
      setState('error')
      return
    }

    if (abortedRef.current) return
    if (!assistantText) {
      setErrorMsg('No reply from Sage')
      setState('error')
      return
    }
    setTurns(prev => [...prev, { role: 'assistant', text: assistantText, ts: Date.now() }])

    setState('reply_playback')
    try { await playTTS(assistantText) } catch { /* ignore, user can read transcript */ }

    if (abortedRef.current) return
    void startListening()
  }

  const pollForReply = async (sid: string | undefined, afterTs: number): Promise<string> => {
    const startedAt = Date.now()
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      if (abortedRef.current) return ''
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
      if (abortedRef.current) return ''
      try {
        const url = sid ? `/api/v1/sage/messages?session_id=${sid}` : '/api/v1/sage/messages'
        const res = await authedFetch(url, {}, { redirectOnAuthFailure: false })
        if (!res.ok) continue
        const data = await res.json() as { messages?: Array<{ role: string; content: string; created_at: string }> }
        const newest = (data.messages || [])
          .filter(m => m.role === 'assistant' && new Date(m.created_at).getTime() > afterTs)
          .pop()
        if (newest?.content) return newest.content
      } catch { /* retry */ }
    }
    return ''
  }

  const playTTS = (text: string) => new Promise<void>((resolve, reject) => {
    (async () => {
      try {
        const a = ttsAudioRef.current
        if (!a) return reject(new Error('no audio element'))
        const res = await authedFetch('/api/v1/sage/tts', {
          method: 'POST',
          body: JSON.stringify({ text, voice }),
        }, { redirectOnAuthFailure: false })
        if (!res.ok) return reject(new Error(`tts ${res.status}`))
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        ttsUrlRef.current = url
        a.src = url
        a.onended = () => { URL.revokeObjectURL(url); ttsUrlRef.current = null; resolve() }
        a.onerror = () => { URL.revokeObjectURL(url); ttsUrlRef.current = null; reject(new Error('playback')) }
        await a.play()
      } catch (e) { reject(e as Error) }
    })()
  })

  const handleStop = () => { stopAll(); onClose() }

  if (!open) return null

  const labelByState: Record<LoopState, string> = {
    initializing: 'Starting…',
    listening: 'Listening',
    speaking: 'Hearing you',
    transcribing: 'Transcribing',
    waiting_reply: 'Sage is thinking',
    reply_playback: 'Sage is speaking',
    error: errorMsg || 'Error',
  }

  // Orb color per state — all on brand palette.
  const orbTone = (() => {
    switch (state) {
      case 'speaking':
      case 'listening':
      case 'reply_playback':
        return 'brand'
      case 'error':
        return 'error'
      default:
        return 'neutral'
    }
  })()

  const orbFill = orbTone === 'brand' ? 'var(--brand-gold)'
    : orbTone === 'error' ? tokens.color.feedback.error
    : 'var(--bg-elevated)'

  const levelScale = state === 'speaking' ? 1 + Math.min(rmsLevel * 3, 0.25) : 1

  // Last one turn of each role — mini transcript fits in the small card
  const lastUser = [...turns].reverse().find(t => t.role === 'user')
  const lastAssistant = [...turns].reverse().find(t => t.role === 'assistant')

  return (
    <div
      // The FAB slot transforms into this card. The transform-origin is the
      // bottom-right so the card appears to grow outward from the FAB
      // position, giving the "FAB expanded" feel.
      style={{
        position: 'fixed',
        bottom: fabBottom,
        right: fabRight,
        width: 260,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: tokens.radius.lg,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        zIndex: 202,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: tokens.typography.fontFamily.sans,
        color: tokens.color.text.primary,
        transformOrigin: 'bottom right',
        animation: 'sage-fab-expand 220ms cubic-bezier(0.2,0.8,0.2,1.0)',
      }}
    >
      {/* Header row — label + close */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: tokens.color.text.muted,
        }}>
          Voice mode
        </div>
        <button
          onClick={handleStop}
          aria-label="End conversation"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            width: 22, height: 22, minHeight: 0, minWidth: 0, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: tokens.color.text.muted,
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* Orb + status */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '16px 10px 12px',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: orbFill,
          boxShadow: state === 'speaking'
            ? `0 0 ${Math.min(24, 8 + rmsLevel * 400)}px ${Math.min(12, 4 + rmsLevel * 200)}px var(--brand-gold)`
            : state === 'reply_playback'
              ? '0 0 20px 6px var(--brand-gold)'
              : '0 0 8px 2px rgba(0,0,0,0.08)',
          transform: `scale(${levelScale})`,
          transition: state === 'speaking'
            ? 'transform 80ms linear, background 250ms'
            : 'transform 250ms cubic-bezier(0.4,0,0.2,1), background 250ms, box-shadow 250ms',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: state === 'listening' ? 'sage-orb-breathe 2.6s ease-in-out infinite' : undefined,
        }}>
          <Mic size={28} color="var(--brand-gold-text)" strokeWidth={2} />
        </div>
        <div style={{
          fontSize: 12, fontWeight: 500, color: tokens.color.text.primary,
          marginTop: 10,
        }}>
          {labelByState[state]}
          {(state === 'transcribing' || state === 'waiting_reply' || state === 'reply_playback')
            ? <span className="sage-dots" aria-hidden>…</span>
            : null}
        </div>
      </div>

      {/* Mini transcript — last user + last assistant */}
      {(lastUser || lastAssistant) && (
        <div style={{
          borderTop: '1px solid var(--border)',
          padding: '8px 10px',
          display: 'flex', flexDirection: 'column', gap: 6,
          maxHeight: 140, overflowY: 'auto',
        }}>
          {lastUser && (
            <div style={{
              alignSelf: 'flex-end',
              maxWidth: '90%',
              padding: '5px 9px',
              borderRadius: 10,
              background: 'var(--brand-gold)',
              color: 'var(--brand-gold-text)',
              fontSize: 11, lineHeight: 1.35,
            }}>
              {lastUser.text}
            </div>
          )}
          {lastAssistant && (
            <div style={{
              alignSelf: 'flex-start',
              maxWidth: '90%',
              padding: '5px 9px',
              borderRadius: 10,
              background: 'var(--bg-elevated)',
              color: tokens.color.text.primary,
              border: '1px solid var(--border)',
              fontSize: 11, lineHeight: 1.35,
            }}>
              {lastAssistant.text}
            </div>
          )}
        </div>
      )}

      {/* Stop button — secondary style, matches composer cancel */}
      <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={handleStop}
          style={{
            width: '100%',
            background: 'transparent',
            border: `1px solid var(--border)`,
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 12, fontWeight: 600,
            color: tokens.color.text.primary,
            cursor: 'pointer',
            fontFamily: tokens.typography.fontFamily.sans,
          }}
        >
          End conversation
        </button>
      </div>

      <audio ref={ttsAudioRef} preload="auto" style={{ display: 'none' }} />

      <style>{`
        @keyframes sage-fab-expand {
          from {
            transform: scale(0.2);
            opacity: 0;
            border-radius: 50%;
          }
          to {
            transform: scale(1);
            opacity: 1;
            border-radius: 10px;
          }
        }
        @keyframes sage-orb-breathe {
          0%,100% { transform: scale(1); box-shadow: 0 0 8px 2px rgba(0,0,0,0.05); }
          50%     { transform: scale(1.05); box-shadow: 0 0 14px 4px var(--brand-gold); }
        }
        @keyframes sage-dot-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }
        .sage-dots { animation: sage-dot-pulse 1.2s infinite; margin-left: 2px; color: var(--text-muted, #64748b); }
      `}</style>
    </div>
  )
}

export default SageConversationMode
